import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { createEffect, ofType, Actions } from "@ngrx/effects";
import { Action, Store } from "@ngrx/store";
import { Observable, exhaustMap, map, catchError, of, switchMap, take, mergeMap } from "rxjs";
import { BasicXpacInfo, DeviceProblem, XpacCompanyInfo, DetailedXpacInfo, LocationHandle, RemoteMaintenanceConnection, XpacConnectionEventInfo, PermissionsApi, RestartMode, DeviceService, LocationService, AuthenticationService, Address, XpacCompanyIdInfo, MenderService, FirmwareService, RemoteFirmwareInformation, Release, TelemetryData, CrossNetDevice, FirmwareInfoPatch, DeviceLogPageResponse } from "src/app/generated/CrossPoint.RemoteXpac.Server";
import { IDataAction } from "src/app/utils/interfaces/common.interfaces";
import { IGetDevicesData, IGetMapDevicesData, IGetRestoreDevicesData, IGetUpgradeData, ISetRestoreDevicesData } from "src/app/utils/interfaces/remote-maintenance.interfaces";
import { IXpacCompanyInfoState, IPatchStoreState, IRemoteXPACPortalState, IGetTelemetry, IFirmwareInfoPatch } from "src/app/utils/interfaces/store.interfaces";
import { errorEventAction } from "../actions/app.actions";
import { DevicesActionsTypes, getDevicesSuccessAction, getDevicesFailedAction, getRestoreDevicesSuccessAction, getRestoreDevicesFailedAction, setRestoreDevicesSuccessAction, setRestoreDevicesFailedAction, getDeviceProblemsSuccessAction, getDeviceProblemsFailedAction, getDeviceLoggingSuccessAction, getDeviceLoggingFailedAction, getDeviceCompaniesSuccessAction, getDeviceCompaniesFailedAction, setDeviceCompaniesSuccessAction, setDeviceCompaniesFailedAction, getStoreInfoSuccessAction, getStoreInfoFailedAction, setStoreInfoSuccessAction, setStoreInfoFailedAction, getLocationAutocompleteSuccessAction, getLocationDataByAddressSuccessAction, getLocationDataByAddressFailedAction, getRemoteConnectionsSuccessAction, getRemoteConnectionsFailedAction, getDeviceConnectionsSuccessAction, getDeviceConnectionsFailedAction, getAuthInfoDevicesSuccessAction, getAuthInfoDevicesFailedAction, restartDeviceSuccessAction, restartDeviceFailedAction, deleteDeviceSuccessAction, deleteDeviceFailedAction, getDevicesAction, getMenderReleasesFailedAction, getMenderReleasesSuccessAction, getUpgradeStatusSuccessAction, getUpgradeStatusFailedAction, installMenderArtifactForDevicesFailedAction, installMenderArtifactForDevicesSuccessAction, getLocationAutocompleteFailedAction, getUpgradeDevicesSuccessAction, getUpgradeDevicesFailedAction, getDeviceFirmwareSuccessAction, getDeviceFirmwareFailedAction, uploadDeviceFirmwareSuccessAction, uploadDeviceFirmwareFailedAction, setFirmwareMetadataSuccessAction, setFirmwareMetadataFailedAction, deleteDeviceFirmwareFailedAction, deleteDeviceFirmwareSuccessAction, getDeviceTelemetrySuccessAction, getDeviceTelemetryFailedAction, getXpacDevicesSuccessAction, getXpacDevicesFailedAction, getMapDevicesSuccessAction, getMapDevicesAction, getMapDevicesFailedAction } from "../actions/devices.actions";
import { selectDevicesQuery } from "../selectors/devices.selectors";


@Injectable()
export class DevicesEffects {
  public getDevicesEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(DevicesActionsTypes.GET_DEVICES),
        switchMap(({ data: query }: IDataAction<IGetDevicesData>) =>
          this.deviceService.getDevices(query.search, query.skip, query.maxItems, query.order, query.type)
            .pipe(
              map((devices: BasicXpacInfo[]) => getDevicesSuccessAction({
                data: {
                  query,
                  devices,
                }
              })),
              catchError((error: HttpErrorResponse) => {
                return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetAllLastInstalledDevicesFailed" } }), getDevicesFailedAction())
              })
            )
        )
      )
  );

  public getUpradeDevicesEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(DevicesActionsTypes.GET_UPGRADE_DEVICES),
        switchMap(({ data: { releaseVersion, search, maxItems, skip } }: IDataAction<IGetUpgradeData>) =>
          this.deviceService.getUpgrade(releaseVersion, search, skip, maxItems)
            .pipe(
              map((devices: BasicXpacInfo[]) => getUpgradeDevicesSuccessAction({ data: devices })),
              catchError((error: HttpErrorResponse) => {
                return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetUpgradeDevicesFailed" } }), getUpgradeDevicesFailedAction())
              })
            )
        )
      )
  );

  public refreshLastInstalledDevicesEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(DevicesActionsTypes.REFRESH_DEVICES),
        switchMap(() => {
          let query: IGetDevicesData;
          this.store.select(selectDevicesQuery).pipe(take(1)).subscribe(q => query = q);
          if (query) {
            return of(getDevicesAction({ data: query }));
          }
          else {
            return of(getDevicesFailedAction());
          }
        })
      )
  );

  public getRestoreDevicesEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(DevicesActionsTypes.GET_RESTORE_DEVICES),
        exhaustMap(({ data: { deviceId, search, maxItems } }: IDataAction<IGetRestoreDevicesData>) =>
          this.deviceService.getRestoreDevices(deviceId, search, maxItems)
            .pipe(
              map((lastInstalledDevices: BasicXpacInfo[]) => getRestoreDevicesSuccessAction({ data: { deviceId, devices: lastInstalledDevices } })),
              catchError((error: HttpErrorResponse) => {
                return of(errorEventAction({data: {error: error, translationkey: "StoreActionFailed.GetRestoreDevicesFailed" }}), getRestoreDevicesFailedAction())
              })
            )
        )
      )
  );

    public getMapDevicesEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(DevicesActionsTypes.GET_MAP_DEVICES),
        switchMap(({ data: query }: IDataAction<IGetMapDevicesData>) =>
          this.deviceService.getMapDevices(query.search, query.type)
            .pipe(
              map((devices: BasicXpacInfo[]) => getMapDevicesSuccessAction({
                data: devices
              })),
              catchError((error: HttpErrorResponse) => {
                return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetMapDevicesFailed" } }), getMapDevicesFailedAction())
              })
            )
        )
      )
  );

  public setRestoreDeviceEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(DevicesActionsTypes.SET_RESTORE_DEVICE),
        exhaustMap(({ data: { serialTarget, serialOld } }: IDataAction<ISetRestoreDevicesData>) =>
          this.deviceService.replaceDevice(serialTarget, serialOld)
            .pipe(
              map(() => setRestoreDevicesSuccessAction()),
              catchError((error: HttpErrorResponse) => {
                return of(errorEventAction({data: {error: error, translationkey: "StoreActionFailed.SetRestoreDeviceFailed" }}), setRestoreDevicesFailedAction())
              })
            )
        )
      )
  );

  public getDeviceProblemsEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(DevicesActionsTypes.GET_DEVICE_PROBLEMS),
        exhaustMap(({ data: deviceId }: IDataAction<string>) => this.deviceService.getProblems(deviceId)
          .pipe(
            map((problems: DeviceProblem[]) => getDeviceProblemsSuccessAction({ data: { deviceId, problems } })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetDeviceProblemsFailed" } }), getDeviceProblemsFailedAction())
            })
          )
        )
      )
  );

  public getXpacDevicesEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(DevicesActionsTypes.GET_CROSSNET_DEVICES),
        switchMap(({ data: deviceId }: IDataAction<string>) => this.deviceService.getXpacDevices(deviceId)
          .pipe(
            map((devices: CrossNetDevice[]) => getXpacDevicesSuccessAction({ data: { deviceId, devices } })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetXpacDevicesFailed" } }), getXpacDevicesFailedAction({ data: deviceId }))
            })
          )
        )
      )
  );

  public getDeviceLoggingEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(DevicesActionsTypes.GET_DEVICE_LOGGING),
        exhaustMap(({ data }: IDataAction<{
          deviceId: string;
          maxLogs: number;
          continuationToken?: string;
          append?: boolean;
        }>) =>
          this.deviceService
            .getLogging(data.deviceId, data.maxLogs, data.continuationToken)
            .pipe(
              map((page: DeviceLogPageResponse) =>
                getDeviceLoggingSuccessAction({
                  data: {
                    deviceId: data.deviceId,
                    page,
                    append: data.append ?? false
                  }
                })
              ),
              catchError((error: HttpErrorResponse) => {
                return of(
                  errorEventAction({
                    data: {
                      error,
                      translationkey: "StoreActionFailed.GetDeviceLoggingFailed"
                    }
                  }),
                  getDeviceLoggingFailedAction()
                );
              })
            )
        )
      )
  );

  public getDeviceCompaniesEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(DevicesActionsTypes.GET_DEVICE_COMPANIES),
        exhaustMap(({ data: deviceId }: IDataAction<string>) => this.deviceService.getDeviceCompanies(deviceId)
          .pipe(
            map((companies: XpacCompanyInfo) => getDeviceCompaniesSuccessAction({ data: { deviceId, companies } })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetDeviceProblemsFailed" } }), getDeviceCompaniesFailedAction())
            })
          )
        )
      )
  );

  private toXpacCompanyIdInfo(companies: XpacCompanyInfo): XpacCompanyIdInfo {
    return {
      partnerId: companies.partner.companyId,
      retailerId: companies.retailer.companyId,
      linkedCompanyIds: companies.linkedCompanies.map(c => c.companyId),
    };
  };

  public setDeviceCompaniesEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(DevicesActionsTypes.SET_DEVICE_COMPANIES),
        exhaustMap(({ data: { deviceId, companies } }: IDataAction<IXpacCompanyInfoState>) =>
          this.deviceService.setDeviceCompanies(deviceId, this.toXpacCompanyIdInfo(companies))
          .pipe(
            map(() => setDeviceCompaniesSuccessAction({ data: { deviceId, companies } })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.SetDeviceCompaniesFailed" } }), setDeviceCompaniesFailedAction())
            })
          )
        )
      )
  );

  public getDeviceTelemetryEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(DevicesActionsTypes.GET_DEVICE_TELEMETRY),
        exhaustMap(({ data: { deviceId, maxEvents } }: IDataAction<IGetTelemetry>) => this.deviceService.getDeviceTelemetry(deviceId, null, maxEvents)
          .pipe(
            map((telemetry: TelemetryData[]) => getDeviceTelemetrySuccessAction({ data: { deviceId, telemetry } })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetDeviceTelemetryFailed" } }), getDeviceTelemetryFailedAction({ data: deviceId }))
            })
          )
        )
      )
  );


  public getStoreInfoEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(DevicesActionsTypes.GET_STORE_INFO),
        exhaustMap(({ data }) => this.deviceService.getStore(data)
          .pipe(
            map((detailedXpacInfo: DetailedXpacInfo) => getStoreInfoSuccessAction({ data: detailedXpacInfo })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetStoreInfoFailed" } }), getStoreInfoFailedAction())
            })
          )
        )
      )
  );

  public setStoreInfoEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(DevicesActionsTypes.SET_STORE_INFO),
        exhaustMap(({ data: { deviceId, patchStoreInfo } }: IDataAction<IPatchStoreState>) =>
          this.deviceService.updateStore(deviceId, patchStoreInfo)
            .pipe(
              map(() => setStoreInfoSuccessAction({ data: { deviceId, patchStoreInfo } })),
              catchError((error: HttpErrorResponse) => {
                return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.SetStoreInfoFailed" } }), setStoreInfoFailedAction())
              })
            )
        )
      )
  );

  public getLocationAutocompleteEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(DevicesActionsTypes.GET_LOCATION_AUTOCOMPLETE),
        switchMap(({ data }) => this.locationService.autocomplete(data)
            .pipe(
              map((autocompleteLocationData: LocationHandle[]) => getLocationAutocompleteSuccessAction({ data: autocompleteLocationData })),
              catchError((error: HttpErrorResponse) => {
                return of(getLocationAutocompleteFailedAction())
              })
            )
        )
      )
  );

  public getLocationDataByAddressEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(DevicesActionsTypes.GET_LOCATION_DATA_BY_ADDRESS),
        exhaustMap(({ data }) => this.locationService.getAddress(data)
          .pipe(
            map((address: Address) => getLocationDataByAddressSuccessAction({ data: address })),
            catchError((error: HttpErrorResponse) => {
              return of(getLocationDataByAddressFailedAction())
            })
          )
        )
      )
  );

  public getRemoteConnectionsEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(DevicesActionsTypes.GET_REMOTE_CONNECTIONS),
        exhaustMap(({ data: deviceId }: IDataAction<string>) => this.deviceService.getRemoteMaintenanceConnections(deviceId)
          .pipe(
            map((connections: RemoteMaintenanceConnection[]) => getRemoteConnectionsSuccessAction({ data: { deviceId, connections } })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetRemoteConnectionsFailed" } }), getRemoteConnectionsFailedAction())
            })
          )
        )
      )
  );

  public getDeviceConnectionsEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(DevicesActionsTypes.GET_DEVICE_CONNECTIONS),
        exhaustMap(({ data: deviceId }: IDataAction<string>) => this.deviceService.getConnections(deviceId)
          .pipe(
            map((events: XpacConnectionEventInfo[]) => getDeviceConnectionsSuccessAction({ data: { deviceId, events } })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetDeviceConnectionsFailed" } }), getDeviceConnectionsFailedAction())
            })
          )
        )
      )
  );

  public getAuthInfoDevicesEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(DevicesActionsTypes.GET_AUTH_INFO_DEVICES),
        exhaustMap(() => this.authenticationService.getPermissions()
          .pipe(
            map((permissions: PermissionsApi) => getAuthInfoDevicesSuccessAction({ data: permissions.permissions })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetAuthInfoDevicesErrorHasOccurred" } }), getAuthInfoDevicesFailedAction())
            })
          )
        )
      )
  );

  public restartDeviceEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(DevicesActionsTypes.RESTART_DEVICE),
        exhaustMap(({ data }) => this.deviceService.restartDevice(data, RestartMode.Device)
          .pipe(
            map(() => restartDeviceSuccessAction({ data })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.RestartDeviceErrorHasOccurred" } }), restartDeviceFailedAction(({ data })))
            })
          )
        )
      )
  );

  public deleteDeviceEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(DevicesActionsTypes.DELETE_DEVICE),
        exhaustMap(({ data }) => this.deviceService.deleteXpac(data)
          .pipe(
            map(() => deleteDeviceSuccessAction()),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.DeleteDeviceErrorHasOccurred" } }), deleteDeviceFailedAction())
            })
          )
        )
      )
  );

  public getMenderReleasesEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(DevicesActionsTypes.GET_MENDER_RELEASES),
        exhaustMap(({ data: { filter, page, pageSize } }) => this.menderService.getReleases(filter, page, pageSize)
          .pipe(
            map((releases: Release[]) => getMenderReleasesSuccessAction({ data: releases })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetMenderArtifactsErrorHasOccurred" } }), getMenderReleasesFailedAction())
            })
          )
        )
      )
  );

  private dictToArray<T, V>(dict: { [key: string]: T }, map: (key: string, value: T) => V): V[] {
    let result: V[] = [];
    for (const key in dict) {
      result.push(map(key, dict[key]));
    }
    return result;
  }

  public getUpgradeStatusEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(DevicesActionsTypes.GET_UPGRADE_STATUS),
        mergeMap(({ data }) => this.menderService.getDeploymentStatuses(data)
          .pipe(
            map((deploymentInfo) => this.dictToArray(deploymentInfo, (deviceId, deployment) => ({
              deviceId,
              deployment,
            }))),
            map((deploymentInfo) => getUpgradeStatusSuccessAction({ data: deploymentInfo })),
            catchError((error: HttpErrorResponse) => {
              // We ignore errors to prevent getting spammed with them on develop where many 'devices' are not in Mender
              return of(getUpgradeStatusFailedAction({ data }))
            })
          )
        )
      )
  );

  public installMenderArtifactForDevicesEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(DevicesActionsTypes.INSTALL_MENDER_ARTIFACT_FOR_DEVICES),
        exhaustMap(({ data }) => this.menderService.upgrade(data)
          .pipe(
            map(() => installMenderArtifactForDevicesSuccessAction()),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({
                data: {
                  error: error,
                  translationkey: 'InstallMenderArtifactForDevicesStatusErrorHasOccurred',
                  translationErrorkey: 'InstallMenderArtifactForDevicesStatus',
                }
              }), installMenderArtifactForDevicesFailedAction())
            })
          )
        )
      )
  );

  public getDeviceFirmwareEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(DevicesActionsTypes.GET_DEVICE_FIRMWARE),
        exhaustMap(() => this.firmwareService.getFirmware()
          .pipe(
            map((firmware: RemoteFirmwareInformation[]) => getDeviceFirmwareSuccessAction({ data: firmware })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetDeviceFirmwareFailed" } }), getDeviceFirmwareFailedAction())
            })
          )
        )
      )
  );

  public uploadDeviceFirmwareEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DevicesActionsTypes.UPLOAD_DEVICE_FIRMWARE),
      exhaustMap(({ data }) => this.firmwareService.uploadFirmware(data)
        .pipe(
          mergeMap((firmware) => [uploadDeviceFirmwareSuccessAction({ data: firmware })]),
          catchError((error: HttpErrorResponse) => {
            return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.UploadDeviceFirmwareFailed" } }), uploadDeviceFirmwareFailedAction())
          })
        )
      )
    )
  );

  public setDeviceFirmwareAccessEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DevicesActionsTypes.SET_DEVICE_FIRMWARE_METADATA),
      mergeMap(({ data }: IDataAction<IFirmwareInfoPatch>) => this.firmwareService.patchFirmwareMetadata(data.id, <FirmwareInfoPatch>data)
        .pipe(
          mergeMap(() => [setFirmwareMetadataSuccessAction({ data: data })]),
          catchError((error: HttpErrorResponse) => {
            return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.SetDeviceFirmwareAccessFailed" } }), setFirmwareMetadataFailedAction({ data: data }))
          })
        )
      )
    )
  );

  public deleteDeviceFirmwareEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DevicesActionsTypes.DELETE_DEVICE_FIRMWARE),
      mergeMap(({ data }: IDataAction<string>) => this.firmwareService.deleteFirmware(data)
        .pipe(
          mergeMap(() => [deleteDeviceFirmwareSuccessAction({ data: data })]),
          catchError((error: HttpErrorResponse) => {
            return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.DeleteDeviceFirmwareFailed" } }), deleteDeviceFirmwareFailedAction({ data: data }))
          })
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private deviceService: DeviceService,
    private firmwareService: FirmwareService,
    private locationService: LocationService,
    private menderService: MenderService,
    private authenticationService: AuthenticationService,
    private store: Store<IRemoteXPACPortalState>,
  ) { }
}
